import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginFailed, loginSuccess } from "../../store/slices/userSlice";
import { loginApi, microsoftLogin } from "../../service/login/loginService";
import { useEffect, useState } from "react";
import { setLogin } from "../../store/slices/loginSlice";
import { useMsal } from "@azure/msal-react";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { isTenantAvailable } from "../../service/tenant/TenantService";
import { getPermissionByUserAndRole } from "../../service/permission/permissionService";
import { setPermissions } from "../../store/slices/permissionSlice";
import { MicrosoftLoginProps, LoginResult } from "../../types/login.types";
import { clearTenancyName, setTenancyName } from "../../store/slices/tenantSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { setFeatures } from "../../store/slices/featureSlice";
import { getAllFeaturesByEditionId } from "../../service/edition/EditionService";
import { bootstrapThemeFromStorage, resolveStoredTheme } from "../../utils/applyTheme";

export const LoginForm = () => {
  const userInfo = useSelector((state: any) => state.signUp);
  const [email, setEmail] = useState(userInfo.email || "");
  const [password, setPassword] = useState(userInfo.password || "");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [emailLengthError, setEmailLengthError] = useState("");
  const tenancyName = useSelector((state: any) => state.tenant.tenancyName);
  const [pwdLengthError, setPwdLengthError] = useState("");
  const [disableBtn, setDisableBtn] = useState(false);
  const [passwordType, setPasswordType] = useState("password");
  const [isRemember, setIsRemember] = useState<boolean>(false);
  const [user, setUser] = useState<any>([]);
  const [tenant, setTenant] = useState<string | null>(tenancyName);
  const [inputValue, setInputValue] = useState<string | null>(tenancyName);
  const [openDialog, setOpenDialog] = useState(false);
  const [tenantErr, setTenantErr] = useState<string>("");
  const { instance } = useMsal();
  const [checked, setChecked] = useState<boolean>(false);
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({
    severity: "",
    summary: "",
    detail: "",
  });
  const localization = useSelector((state: any) => state.localization);
  // Resolve the saved theme's mode once (synchronously) so the brand panel can
  // render light-on-light for light themes and dark for dark themes without a
  // flash. bootstrapThemeFromStorage() then paints the CSS variables on mount.
  const [isDarkTheme] = useState<boolean>(
    () => resolveStoredTheme().mode === "dark"
  );

  const handleLogin = async () => {
    try {
      const loginResponse = await instance.loginPopup({
        scopes: ["user.read"],
      });

      const authToken = loginResponse?.idToken;
      if (!authToken) return;
      const credentials: MicrosoftLoginProps = {
        token: authToken,
        tenancyName: tenant,
      };

      const res: any = await microsoftLogin(credentials);
      if (res.data.Result == null) {
        dispatch(loginFailed(localization?.LoginFailed || "Login failed!"));
        showToastMessage(
          "error",
          localization?.Error || "Error",
          res?.data?.Error || localization?.LoginFailed || "Login failed!"
        );
        setDisableBtn(false);
        return;
      }
      const result = res?.data?.Result;
      afterLoginProcess(result);
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const handleSettingsVal = (input: any[]) => {
    const keys = ["Language", "Mode", "Theme"];
    keys.forEach((val) => {
      const findVal = input.find((k) => val.includes(k.name));
      localStorage.setItem(findVal.name, findVal.value);
    });
  };

  const handleChangeTenantInputBox = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setInputValue(e.target.value);
    // if (e.target.value.trim() === "") {
    //   setChecked(true);
    // } else {
    //   setChecked(false);
    // }
  };

  const handleValidation = () => {
    let err: string = "";
    if (email.length === 0 && password.length === 0) {
      setPwdLengthError(
        localization?.PasswordFieldIsRequired || "Password field is required"
      );
      setEmailLengthError(
        localization?.EmailFieldIsRequired || "Email field is required"
      );
      err = "error";
    }

    if (email.length === 0) {
      setEmailLengthError(
        localization?.EmailFieldIsRequired || "Email field is required"
      );
      err = "error";
    }
    if (password.length === 0) {
      setPwdLengthError(
        localization?.PasswordFieldIsRequired || "Password field is required"
      );
      err = "error";
    }
    if (email.length > 0 && email.replace(/\s/g, "").length == 0) {
      setEmailLengthError(
        localization?.PleaseEnterValidInput || "Please enter a valid input"
      );
      err = "error";
    }
    if (password.length > 0 && password.replace(/\s/g, "").length == 0) {
      setPwdLengthError(
        localization?.PleaseEnterValidInput || "Please enter a valid input"
      );
      err = "error";
    }

    if (err.length > 0) {
      return false;
    }

    setEmailLengthError("");
    setPwdLengthError("");
    return true;
  };

  const handleClick = async () => {
    const validate = handleValidation();
    if (!validate) return;

    setDisableBtn(true);

    const credentials = {
      EmailOrUserName: email,
      Password: password,
      // TenancyName: checked ? null : tenant,
      TenancyName: tenant?.length === 0 ? null : tenant,
      RememberMe: isRemember,
    };
    try {
      const res: any = await loginApi(credentials);

      if (res.data.Result == null) {
        dispatch(loginFailed(localization?.LoginFailed || "Login failed!"));
        showToastMessage(
          "error",
          localization?.Error || "Error",
          res?.data?.Error || localization?.LoginFailed || "Login failed!"
        );
        setDisableBtn(false);
        return;
      }
      const result = res?.data?.Result;
      afterLoginProcess(result);
    } catch (error: any) {
      dispatch(loginFailed(error));
      showToastMessage(
        "error",
        localization?.Error || "Error",
        error?.response?.data.Error
      );
    }

    setDisableBtn(false);
  };

  const handleToggle = () => {
    if (passwordType === "password") {
      setPasswordType("text");
    } else {
      setPasswordType("password");
    }
  };

  const getFeaturesByEdition = async (editionId: number) => {
    if (!editionId) return;

    const features = await getAllFeaturesByEditionId(dispatch, editionId);

    if (!features || features?.length === 0) {
      return;
    }

    dispatch(setFeatures(features || []));
  };

  const afterLoginProcess = async (result: LoginResult) => {
    const tenantId = result?.UserDetails?.tenantId as unknown as number | null;
    setTenantId(tenantId ?? null);
    const userDetails = result?.UserDetails;
    const authResult = result?.AuthenticateResult;
    const settings = result?.Settings;
    const roleName = userDetails?.Roles[0]?.name;
    const email: string = userDetails?.emailAddress;
    dispatch(loginSuccess(email));

    if (isRemember) {
      localStorage.setItem("rememberMe", isRemember.toString());
    }
    else {
      localStorage.setItem("rememberMe", "false");
    }
    // Mark this browser session as active. sessionStorage is wiped when the
    // browser is closed, so when "Keep me signed in" is unchecked we detect its
    // absence on the next load and refuse to restore the session.
    sessionStorage.setItem("sessionActive", "true");
    // 👉 Calculate actual expiry time in milliseconds from now
    const expTimeInMinutes = authResult?.ExpTimeInMinutes;
    const expTimeInMillis = Date.now() + (expTimeInMinutes ?? 0) * 60 * 1000;
    localStorage.setItem("tenantId", tenantId?.toString() || "null");
    localStorage.setItem("role", roleName);
    localStorage.setItem(
      "roleIds",
      JSON.stringify(userDetails?.Roles?.map((role) => role.id))
    );
    localStorage.setItem("userId", JSON.stringify(userDetails?.id));
    // localStorage.setItem("TokenExpTime", expTimeInMillis?.toString());
    // store access token in memory or localStorage if rememberMe true

    localStorage.setItem("token", authResult?.BearerToken);
    localStorage.setItem("TokenExpTime", expTimeInMillis?.toString());

    // localStorage.setItem("token", authResult?.BearerToken);
    dispatch(setLogin({ islogin: true }));

    if (settings?.length > 0) {
      handleSettingsVal(settings);
    }
    const editionId = userDetails?.editionId;
    localStorage.setItem("editionId", editionId?.toString());


    if (!editionId && tenantId !== null) {
      localStorage.setItem("path", "subscription-plan");
      navigate("/subscription-plan");
      return;
    }
    const permissionInputs = {
      userId: userDetails.id,
      tenantId: tenantId as unknown as number,
      roleIds: userDetails.Roles.map((role) => role.id),
    };
    const loggedInUserPermissions = await getPermissionByUserAndRole(
      dispatch,
      permissionInputs
    );

    if (!loggedInUserPermissions || loggedInUserPermissions?.length === 0) {
      return navigate("/permission-denied");
    }
    //add features
    getFeaturesByEdition(editionId);
    dispatch(setFeatures(loggedInUserPermissions?.features || []));
    dispatch(setPermissions(loggedInUserPermissions));
    navigate("/dashboard");
  };

  const handleEnterKey = (event: any) => {
    if (event.key === "Enter") {
      handleClick();
    }
  };

  // useEffect(() => {
  //   if (user) {
  //     axios
  //       .get("https://www.googleapis.com/oauth2/v3/userinfo", {
  //         headers: { Authorization: `Bearer ${user.access_token}` },
  //       })
  //       .then((res) => {
  //         console.log(res);
  //       })
  //       .catch((err) => console.log(err));
  //   }
  // }, [user]);

  const googleLogin = useGoogleLogin({
    onSuccess: async ({ code }) => {
      try {
        const tokens = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/Authentication/googleLogin`,
          {
            code: code,
            tenancyName: tenant,
          }
        );
        if (tokens.data.Result == null) {
          dispatch(loginFailed(localization?.LoginFailed || "Login failed!"));
          showToastMessage(
            "error",
            localization?.Error || "Error",
            tokens?.data?.Error || localization?.LoginFailed || "Login failed!"
          );
          setDisableBtn(false);
          return;
        }
        if (tokens?.data?.Result) {
          afterLoginProcess(tokens?.data?.Result);
        }
      } catch (error) {
        showToastMessage(
          "error",
          localization?.Error || "Error",
          localization?.GoogleLoginFailed || "Google login failed"
        );
        console.error(error);
      }
    },
    flow: "auth-code",
    scope: "email profile",
    redirect_uri: `${process.env.REACT_APP_UI_BASE_URL}/auth/google/callback`,
  });

  const handleOpenDialog = () => {
    setTenantErr("");
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    // setChecked(false);
    setTenantErr("");
    setInputValue(tenant ?? "");
    setOpenDialog(false);

  };

  // const handleSaveTenant = async () => {
  //   // const newTenant = checked ? null : inputValue;
  //   const newTenant = inputValue?.trim();
  //   console.log("newTenant", newTenant);
  //   if (newTenant === undefined) {
  //     showToastMessage(
  //       "error",
  //       "Error!",
  //       "Tenancy name is required!"
  //     );
  //     return;
  //   }
  //   // if (!newTenant) {
  //   //   setTenant(null);
  //   //   setOpenDialog(false);

  //   // }
  //   if (newTenant?.length === 0) {
  //     setTenant(null);
  //     // setOpenDialog(false);
  //     // return;
  //   }
  //   try {
  //     if(newTenant?.length === 0){
  //     const res: any = await isTenantAvailable({
  //       tenancyName: newTenant?.length>0?newTenant:null,
  //     });
  //     if (res.data.Result) {
  //       console.log(res?.data?.Result);
  //       setTenantId(res?.data?.Result?.id);
  //       setTenant(res?.data?.Result?.tenancyName);
  //       dispatch(
  //         setTenancyName({ tenancyName: res?.data?.Result?.tenancyName })
  //       );
  //       showToastMessage(
  //         "success",
  //         localization?.Success || "Success",
  //         localization?.TenantSavedSuccessfully || "Tenant saved successfully!"
  //       );
  //       localStorage.setItem(
  //         "tenantId",
  //         res?.data?.Result?.id.toString() || "1"
  //       );
  //       handleCloseDialog();
  //       return;
  //     }
  //     showToastMessage(
  //       "error",
  //       localization?.Error || "Error",
  //       localization?.TenantNotPresent || "Tenant not present!"
  //     );
  //   } else {

  //   }
  //   } catch (error: any) {
  //     showToastMessage(
  //       "error",
  //       localization?.Error || "Error",
  //       localization?.AnErrorOccurred || "An error occurred!"
  //     );
  //   }
  // };
  const handleSaveTenant = async () => {
    const newTenant = inputValue?.trim();
    if (newTenant === undefined) {
      showToastMessage(
        "error",
        "Error!",
        "Tenancy name is required!"
      );
      return;
    }
    // If input is empty or null
    if (newTenant?.length === 0) {
      setTenant(null);
      setInputValue("");
      setTenantId(null);
      dispatch(clearTenancyName());
      localStorage.setItem("tenantId", "null"); // store as string "null" if needed
      handleCloseDialog();
      showToastMessage(
        "success",
        localization?.Success || "Success",
        localization?.TenantSavedSuccessfully || "Tenant saved successfully!"
      );
      return;
    }

    try {
      // Call API only if tenant name is provided
      const res: any = await isTenantAvailable({
        tenancyName: newTenant,
      });

      if (res.data.Result) {
        setTenantId(res?.data?.Result?.id);
        setTenant(res?.data?.Result?.tenancyName);
        dispatch(
          setTenancyName({ tenancyName: res?.data?.Result?.tenancyName })
        );
        localStorage.setItem(
          "tenantId",
          res?.data?.Result?.id.toString() || "1"
        );
        handleCloseDialog();
        showToastMessage(
          "success",
          localization?.Success || "Success",
          localization?.TenantSavedSuccessfully || "Tenant saved successfully!"
        );
        return;
      }

      showToastMessage(
        "error",
        localization?.Error || "Error",
        localization?.TenantNotPresent || "Tenant not present!"
      );
    } catch (error: any) {
      showToastMessage(
        "error",
        localization?.Error || "Error",
        localization?.AnErrorOccurred || "An error occurred!"
      );
    }
  };

  const showToastMessage = (
    severity: string,
    summary: string,
    detail: string
  ) => {
    setToastMessage({ severity, summary, detail });
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  // Apply the last-saved theme's brand accent so the login splash matches the
  // colour the user picked before signing out (LoginForm renders outside
  // MainLayout, so getUserTheme() never runs here).
  useEffect(() => {
    bootstrapThemeFromStorage();
  }, []);

  useEffect(() => {
    if (tenancyName && tenancyName.length > 0) {
      setInputValue(tenancyName);
    } else {
      setInputValue("");
    }

  }, [tenancyName]);
  useEffect(() => {
    if (inputValue?.length === 0) {
      setTenant(null);
    }
  }, [inputValue]);

  // Brand-panel styling adapts to the theme mode: a light, tinted panel with
  // dark text for light themes; the rich primary→charcoal panel with white text
  // for dark themes.
  // The page backdrop (behind the card) is light for light themes so the card
  // floats on a clean surface; dark themes keep the rich primary→charcoal one.
  const pageBgGradient = isDarkTheme
    ? "from-[var(--primaryColor)] via-slate-600 to-[var(--charcoal)]"
    : "from-[var(--boxShadow)] via-slate-50 to-[var(--boxShadow)]";
  const brandPanelGradient = isDarkTheme
    ? "from-[var(--primaryColor)] to-[var(--charcoal)]"
    : "from-white to-[var(--boxShadow)]";
  const brandPanelText = isDarkTheme ? "text-white" : "text-[#2c2419]";
  const brandDotColor = isDarkTheme ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)";
  const brandLogoTile = isDarkTheme
    ? "bg-gradient-to-br from-white/25 to-white/10 border border-white/30 backdrop-blur-sm"
    : "bg-gradient-to-br from-[var(--primaryColor)] to-[var(--charcoal)] border border-white/20";
  const brandHeadingGradient = isDarkTheme
    ? "from-white to-[rgba(255,255,255,0.75)]"
    : "from-[var(--charcoal)] to-[var(--primaryColor)]";

  return (
    <>
      {/* Add animations that can't be directly represented in Tailwind */}
      <style>
        {`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        
        @keyframes containerFadeIn {
            from {
                opacity: 0;
                transform: translateY(30px) scale(0.95);
            }
            to {
                opacity: 1;
                transform: translateY(0) scale(1);
            }
        }

        @keyframes floatPattern {
            0%, 100% { transform: translate(-10px, -10px) rotate(0deg); }
            25% { transform: translate(10px, -15px) rotate(1deg); }
            50% { transform: translate(-5px, 10px) rotate(-1deg); }
            75% { transform: translate(15px, 5px) rotate(0.5deg); }
        }

        @keyframes slideInLeft {
            from {
                opacity: 0;
                transform: translateX(-30px);
            }
            to {
                opacity: 1;
                transform: translateX(0);
            }
        }

        @keyframes slideInRight {
            from {
                opacity: 0;
                transform: translateX(30px);
            }
            to {
                opacity: 1;
                transform: translateX(0);
            }
        }

        @keyframes slideDown {
            from {
                opacity: 0;
                transform: translateY(-10px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }

        @keyframes scaleIn {
            from { 
                opacity: 0;
                transform: scale(0.9);
            }
            to { 
                opacity: 1;
                transform: scale(1);
            }
        }

        @keyframes spin {
            0% { transform: translate(-50%, -50%) rotate(0deg); }
            100% { transform: translate(-50%, -50%) rotate(360deg); }
        }

        @keyframes slideInDown {
            from {
                opacity: 0;
                transform: translateY(-20px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        `}
      </style>

      {/* Loading spinner */}
      {disableBtn && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm">
          <div className="w-10 h-10 border-3 border-white border-opacity-30 rounded-full border-t-white animate-spin"></div>
        </div>
      )}

      {/* Toast notifications */}
      {showToast && (
        <div
          className={`fixed top-5 right-5 z-50 p-4 rounded-xl shadow-lg flex items-center max-w-md ${toastMessage.severity === "error"
            ? "bg-red-50 border-l-4 border-red-500 text-red-700"
            : "bg-green-50 border-l-4 border-green-500 text-green-700"
            }`}
          style={{ animation: "slideInDown 0.3s ease-out forwards" }}
        >
          <div className="mr-3 text-xl">
            {toastMessage.severity === "error" ? (
              <i className="fas fa-exclamation-circle"></i>
            ) : (
              <i className="fas fa-check-circle"></i>
            )}
          </div>
          <div className="flex-1">
            <div className="font-semibold">{toastMessage.summary}</div>
            <div className="text-sm opacity-90">{toastMessage.detail}</div>
          </div>
        </div>
      )}

      <div className={`min-h-screen flex items-center justify-center p-5 bg-gradient-to-br ${pageBgGradient} font-['Inter',sans-serif] relative overflow-x-hidden`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,var(--boxShadow)_0%,transparent_50%),radial-gradient(circle_at_80%_20%,var(--boxShadow)_0%,transparent_50%),radial-gradient(circle_at_40%_40%,var(--boxShadow)_0%,transparent_50%)] z-0"></div>

        <div
          className="bg-white rounded-3xl shadow-2xl overflow-hidden max-w-[1100px] w-full min-h-[650px] flex relative z-10 backdrop-blur-md lg:flex-row flex-col"
          style={{ animation: "containerFadeIn 0.8s ease-out" }}
        >
          {/* Left Side - Brand Content */}
          <div className={`flex-[1.2] bg-gradient-to-br ${brandPanelGradient} ${brandPanelText} lg:p-[30px] md:p-10 p-10 flex flex-col justify-center items-center text-center relative overflow-hidden`}>
            <div
              className="absolute inset-0 opacity-40 z-[1]"
              style={{
                background: `url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Cdefs%3E%3Cpattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse"%3E%3Ccircle cx="10" cy="10" r="1" fill="${brandDotColor}"%3E%3C/circle%3E%3C/pattern%3E%3C/defs%3E%3Crect width="100" height="100" fill="url(%23dots)"%3E%3C/rect%3E%3C/svg%3E')`,
                animation: "floatPattern 20s ease-in-out infinite",
              }}
            ></div>

            <div
              className="relative z-[2]"
              style={{ animation: "slideInLeft 0.8s ease-out 0.2s both" }}
            >
              <div className={`w-[60px] md:w-[90px] h-[60px] md:h-[90px] ${brandLogoTile} md:rounded-3xl rounded-xl flex items-center justify-center mx-auto mb:mb-9 mb-4 shadow-lg relative overflow-hidden transition-all duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] hover:translate-y-[-5px] hover:scale-105 hover:shadow-xl`}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 384 512"
                  fill="white"
                  className="relative z-[1] w-[30px] md:w-[42px] h-[30px] md:h-[42px]"
                >
                  <path d="M64 0C28.7 0 0 28.7 0 64L0 448c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-288-128 0c-17.7 0-32-14.3-32-32L224 0 64 0zM256 0l0 128 128 0L256 0zM64 80c0-8.8 7.2-16 16-16l64 0c8.8 0 16 7.2 16 16s-7.2 16-16 16L80 96c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16l64 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-64 0c-8.8 0-16-7.2-16-16zm128 72c8.8 0 16 7.2 16 16l0 17.3c8.5 1.2 16.7 3.1 24.1 5.1c8.5 2.3 13.6 11 11.3 19.6s-11 13.6-19.6 11.3c-11.1-3-22-5.2-32.1-5.3c-8.4-.1-17.4 1.8-23.6 5.5c-5.7 3.4-8.1 7.3-8.1 12.8c0 3.7 1.3 6.5 7.3 10.1c6.9 4.1 16.6 7.1 29.2 10.9l.5 .1s0 0 0 0s0 0 0 0c11.3 3.4 25.3 7.6 36.3 14.6c12.1 7.6 22.4 19.7 22.7 38.2c.3 19.3-9.6 33.3-22.9 41.6c-7.7 4.8-16.4 7.6-25.1 9.1l0 17.1c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-17.8c-11.2-2.1-21.7-5.7-30.9-8.9c0 0 0 0 0 0c-2.1-.7-4.2-1.4-6.2-2.1c-8.4-2.8-12.9-11.9-10.1-20.2s11.9-12.9 20.2-10.1c2.5 .8 4.8 1.6 7.1 2.4c0 0 0 0 0 0s0 0 0 0s0 0 0 0c13.6 4.6 24.6 8.4 36.3 8.7c9.1 .3 17.9-1.7 23.7-5.3c5.1-3.2 7.9-7.3 7.8-14c-.1-4.6-1.8-7.8-7.7-11.6c-6.8-4.3-16.5-7.4-29-11.2l-1.6-.5s0 0 0 0c-11-3.3-24.3-7.3-34.8-13.7c-12-7.2-22.6-18.9-22.7-37.3c-.1-19.4 10.8-32.8 23.8-40.5c7.5-4.4 15.8-7.2 24.1-8.7l0-17.3c0-8.8 7.2-16 16-16z" />
                </svg>
              </div>

              <h1 className={`md:text-4xl text-[30px] font-bold mb-[18px] tracking-[-1px] bg-gradient-to-br ${brandHeadingGradient} bg-clip-text text-transparent`}>
                InvoiceProquantic
              </h1>

              <p className="text-lg font-normal opacity-90 leading-relaxed md:mb-[50px] mb-6 max-w-[320px] mx-auto">
                Transform your billing process with intelligent invoicing
                solutions
              </p>

              <ul className="list-none text-center p-0 max-w-[350px]  w-full">
                <li className="md:py-4 py-3 flex items-center text-base opacity-90 transition-all duration-300 relative hover:opacity-100 hover:translate-x-[5px] group text-center justify-center">
                  <div className="absolute hidden left-[-10px] top-1/2 w-0 h-[2px] bg-slate-500 transform -translate-y-1/2 transition-width duration-300 group-hover:w-[4px]"></div>
                  <i className="fas fa-rocket hidden text-slate-500 mr-[18px] w-6 text-lg transition-all duration-300 group-hover:scale-110"></i>
                  Lightning-fast invoice creation
                </li>
                <li className="md:py-4 py-3 flex items-center text-base opacity-90 transition-all duration-300 relative hover:opacity-100 hover:translate-x-[5px] group text-center justify-center">
                  <div className="absolute hidden left-[-10px] top-1/2 w-0 h-[2px] bg-slate-500 transform -translate-y-1/2 transition-width duration-300 group-hover:w-[4px]"></div>
                  <i className="fas fa-chart- hiddenline text-slate-500 mr-[18px] w-6 text-lg transition-all duration-300 group-hover:scale-110"></i>
                  Real-time payment tracking
                </li>
                <li className="md:py-4 py-3 flex items-center text-base opacity-90 transition-all duration-300 relative hover:opacity-100 hover:translate-x-[5px] group text-center justify-center">
                  <div className="absolute hidden left-[-10px] top-1/2 w-0 h-[2px] bg-slate-500 transform -translate-y-1/2 transition-width duration-300 group-hover:w-[4px]"></div>
                  <i className="fas fa-shield hidden-alt text-slate-500 mr-[18px] w-6 text-lg transition-all duration-300 group-hover:scale-110"></i>
                  Bank-level security & compliance
                </li>
                <li className="md:py-4 py-3 flex items-center text-base opacity-90 transition-all duration-300 relative hover:opacity-100 hover:translate-x-[5px] group text-center justify-center">
                  <div className="absolute hidden left-[-10px] top-1/2 w-0 h-[2px] bg-slate-500 transform -translate-y-1/2 transition-width duration-300 group-hover:w-[4px]"></div>
                  <i className="fas fa-globe  hiddentext-slate-500 mr-[18px] w-6 text-lg transition-all duration-300 group-hover:scale-110"></i>
                  Multi-currency & tax automation
                </li>
                <li className="md:py-4 py-3 flex items-center text-base opacity-90 transition-all duration-300 relative hover:opacity-100 hover:translate-x-[5px] group text-center justify-center">
                  <div className="absolute hidden left-[-10px] top-1/2 w-0 h-[2px] bg-slate-500 transform -translate-y-1/2 transition-width duration-300 group-hover:w-[4px]"></div>
                  <i className="fas fa-mobile hidden-alt text-slate-500 mr-[18px] w-6 text-lg transition-all duration-300 group-hover:scale-110"></i>
                  Mobile-first design
                </li>
              </ul>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="flex-1 md:p-[40px] p-8 flex flex-col justify-center bg-[#faf8f4] relative">
            <div className="absolute top-0 right-0 w-[100px] h-[100px] bg-[radial-gradient(circle,var(--primaryColor)_0%,transparent_70%)] opacity-10 rounded-full"></div>

            <div
              className="text-center md:mb-[10px]"
              style={{ animation: "slideInRight 0.8s ease-out 0.4s both" }}
            >
              <h2 className="text-[32px] font-bold text-[#2c2419] mb-3 tracking-[-0.5px]">
                {localization?.WelcomeBack || "Welcome Back"}
              </h2>
              <p className="text-[#5d5448] text-[17px] font-normal mt-2">
                {localization?.AccessYourInvoicingDashboard ||
                  "Access your invoicing dashboard"}
              </p>
            </div>

            {/* Tenant Selector */}
            <div
              className="bg-[#f5f2ec] rounded-2xl p-4 md:mb-5 mb-5 flex justify-between items-center border-2 border-transparent transition-all duration-400 relative overflow-hidden hover:border-[var(--primaryColor)] hover:bg-white hover:-translate-y-[2px] hover:shadow-lg gap-2"
              style={{ animation: "slideInRight 0.8s ease-out 0.5s both" }}
            >
              <div className="text-[#5d5448] text-[15px] font-medium">
                <strong>{localization?.Organization || "Organization"}:</strong>{" "}
                {tenant ? tenant : localization?.NotSelected || "Not selected"}
              </div>
              <button
                className="bg-gradient-to-br min-w-[100px] from-[var(--primaryColor)] to-[var(--charcoal)] text-white border-none py-[10px] px-5 rounded-xl text-sm font-semibold cursor-pointer transition-all duration-300 relative overflow-hidden hover:-translate-y-[2px] hover:shadow-md"
                onClick={handleOpenDialog}
              >
                <div className="absolute top-1/2 left-1/2 w-0 h-0 bg-white bg-opacity-20 rounded-full transition-all duration-400 transform -translate-x-1/2 -translate-y-1/2 hover:w-[100px] hover:h-[100px]"></div>
                <span className="relative z-10">
                  {localization?.Change || "Change"}
                </span>
              </button>
            </div>

            {/* Social Login */}
            <div
              className="flex gap-[18px] mb-2"
              style={{ animation: "slideInRight 0.8s ease-out 0.6s both" }}
            >
              <button
                className="flex-1 py-[18px] border-2 border-[#f5f2ec] rounded-2xl bg-white cursor-pointer transition-all duration-400 flex items-center justify-center text-xl text-[#5d5448] relative overflow-hidden hover:border-[var(--primaryColor)] hover:-translate-y-[3px] hover:shadow-lg group"
                onClick={handleLogin}
              >
                <div className="absolute top-1/2 left-1/2 w-0 h-0 bg-[#faf8f4] rounded-full transition-all duration-400 transform -translate-x-1/2 -translate-y-1/2 group-hover:w-[200px] group-hover:h-[200px]"></div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                  width="20"
                  height="20"
                  className="relative z-[1] text-[#00a1f1] group-hover:text-[#00a1f1]"
                >
                  <path
                    fill="currentColor"
                    d="M0 32h214.6v214.6H0V32zm233.4 0H448v214.6H233.4V32zM0 265.4h214.6V480H0V265.4zm233.4 0H448V480H233.4V265.4z"
                  />
                </svg>
              </button>
              <button
                className="flex-1 py-[18px] border-2 border-[#f5f2ec] rounded-2xl bg-white cursor-pointer transition-all duration-400 flex items-center justify-center text-xl text-[#5d5448] relative overflow-hidden hover:border-[var(--primaryColor)] hover:-translate-y-[3px] hover:shadow-lg group"
                onClick={googleLogin}
              >
                <div className="absolute top-1/2 left-1/2 w-0 h-0 bg-[#faf8f4] rounded-full transition-all duration-400 transform -translate-x-1/2 -translate-y-1/2 group-hover:w-[200px] group-hover:h-[200px]"></div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 488 512"
                  width="20"
                  height="20"
                  className="relative z-[1] text-[#db4437] group-hover:text-[#db4437]"
                >
                  <path
                    fill="currentColor"
                    d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
                  />
                </svg>
              </button>
            </div>

            {/* Divider */}
            <div
              className="flex items-center my-3 text-[#8b7d6b] text-[15px] font-medium "
              style={{ animation: "slideInRight 0.8s ease-out 0.7s both" }}
            >
              <div className="flex-1 h-[2px] bg-gradient-to-r from-transparent via-[#f5f2ec] to-transparent"></div>
              <span className="px-6 bg-[#faf8f4] relative">
                {localization?.OrContinueWithEmail || "or continue with email"}
              </span>
              <div className="flex-1 h-[2px] bg-gradient-to-r from-transparent via-[#f5f2ec] to-transparent"></div>
            </div>

            {/* Login Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleClick();
              }}
            >
              <div
                className="xl:mb-5 mb-4"
                style={{ animation: "slideInRight 0.8s ease-out 0.8s both" }}
              >
                <label
                  className="block mb-[10px] text-[#2c2419] font-semibold text-[15px] transition-colors duration-300"
                  htmlFor="email"
                >
                  {localization?.EmailOrUsername || "Email or Username"}
                </label>
                <input
                  type="text"
                  id="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailLengthError("");
                  }}
                  onKeyDown={handleEnterKey}
                  className={`w-full py-[18px] px-[22px] border-2 rounded-2xl text-base font-normal transition-all duration-400 bg-white text-[#2c2419] focus:outline-none focus:border-[var(--primaryColor)] focus:shadow-[0_0_0_4px_var(--boxShadow),0_8px_16px_rgba(169,144,128,0.1)] focus:-translate-y-[2px] ${emailLengthError
                    ? "border-[#e74c3c] bg-[#fdf2f2]"
                    : "border-[#f5f2ec]"
                    }`}
                  placeholder={
                    localization?.EnterYourEmailOrUsername ||
                    "Enter your email or username"
                  }
                />
                {emailLengthError && (
                  <div
                    className="text-[#e74c3c] text-[13px] mt-[6px] font-medium"
                    style={{ animation: "slideDown 0.3s ease-out" }}
                  >
                    {emailLengthError}
                  </div>
                )}
              </div>

              <div
                className="xl:mb-7 mb-4"
                style={{ animation: "slideInRight 0.8s ease-out 0.9s both" }}
              >
                <label
                  className="block mb-[10px] text-[#2c2419] font-semibold text-[15px] transition-colors duration-300"
                  htmlFor="password"
                >
                  {localization?.Password || "Password"}
                </label>
                <div className="relative">
                  <input
                    type={passwordType}
                    id="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPwdLengthError("");
                    }}
                    onKeyDown={handleEnterKey}
                    className={`w-full py-[18px] px-[22px] border-2 rounded-2xl text-base font-normal transition-all duration-400 bg-white text-[#2c2419] focus:outline-none focus:border-[var(--primaryColor)] focus:shadow-[0_0_0_4px_var(--boxShadow),0_8px_16px_rgba(169,144,128,0.1)] focus:-translate-y-[2px] ${pwdLengthError
                      ? "border-[#e74c3c] bg-[#fdf2f2]"
                      : "border-[#f5f2ec]"
                      }`}
                    placeholder={
                      localization?.EnterYourPassword || "Enter your password"
                    }
                  />
                  <button
                    type="button"
                    className="absolute right-[13px] top-[13px] transform -translate-y-1/2 bg-none border-none text-[#8b7d6b] cursor-pointer text-base p-2 rounded-lg transition-all duration-300 hover:text-[var(--primaryColor)] bg-transparent"
                    onClick={handleToggle}
                  >
                    <FontAwesomeIcon
                      icon={passwordType === "password" ? faEyeSlash : faEye}
                    />
                  </button>
                </div>
                {pwdLengthError && (
                  <div
                    className="text-[#e74c3c] text-[13px] mt-[6px] font-medium"
                    style={{ animation: "slideDown 0.3s ease-out" }}
                  >
                    {pwdLengthError}
                  </div>
                )}
              </div>

              <div
                className="flex justify-between items-center xl:mb-5 mb-5"
                style={{ animation: "slideInRight 0.8s ease-out 1s both" }}
              >
                <div className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    id="remember"
                    checked={isRemember}
                    onChange={(e) => setIsRemember(e.target.checked)}
                    className="w-5 h-5 border-2 border-[#f5f2ec] rounded-md cursor-pointer appearance-none bg-white transition-all duration-300 relative checked:bg-[var(--primaryColor)] checked:border-[var(--primaryColor)] after:content-['✓'] after:absolute after:top-[2px] after:left-[5px] after:transform after:-translate-x-1/2 after:-translate-y-1/2 after:text-white after:text-xs after:font-bold after:opacity-0 checked:after:opacity-100 shadow-lg"
                  />
                  <label
                    htmlFor="remember"
                    className="text-[#5d5448] md:text-[15px] text-[12px] font-medium cursor-pointer"
                  >
                    {localization?.KeepMeSignedIn || "Keep me signed in"}
                  </label>
                </div>
                <Link
                  to="/forgotPassword"
                  className="text-[var(--primaryColor)] no-underline md:text-[15px] text-[12px] font-semibold transition-all duration-300 relative hover:text-[var(--charcoal)] after:content-[''] after:absolute after:bottom-[-2px] after:left-0 after:w-0 after:h-[2px] after:bg-[var(--primaryColor)] after:transition-width after:duration-300 hover:after:w-full"
                >
                  {localization?.ForgotPassword || "Forgot password?"}
                </Link>
              </div>

              <button
                type="submit"
                className={`w-full py-5 bg-gradient-to-br from-[var(--primaryColor)] to-[var(--charcoal)] text-white border-none rounded-2xl text-[17px] font-semibold cursor-pointer transition-all duration-400 mb-7 relative overflow-hidden hover:-translate-y-[3px] hover:shadow-[0_16px_32px_rgba(201,168,118,0.3)] ${disableBtn ? "pointer-events-none" : ""
                  }`}
                disabled={disableBtn}
                style={{ animation: "slideInRight 0.8s ease-out 1.1s both" }}
              >
                <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white via-opacity-20 to-transparent transition-all duration-600 hover:left-full"></div>
                {disableBtn ? (
                  <div
                    className="absolute top-1/2 left-1/2 w-5 h-5 border-2 border-white border-opacity-30 border-t-white rounded-full animate-spin"
                    style={{ transform: "translate(-50%, -50%)" }}
                  ></div>
                ) : (
                  <span>{localization?.SignIn || "Sign In"}</span>
                )}
              </button>
            </form>

            <div
              className="text-center text-[#8b7d6b] text-[15px] font-medium"
              style={{ animation: "slideInRight 0.8s ease-out 1.2s both" }}
            >
              {localization?.YouDontHaveAnAccount || "Don't have an account?"}{" "}
              <Link
                to="/register"
                className="text-[var(--primaryColor)] no-underline font-semibold transition-all duration-300 relative hover:text-[var(--charcoal)] after:content-[''] after:absolute after:bottom-[-2px] after:left-0 after:w-0 after:h-[2px] after:bg-[var(--primaryColor)] after:transition-width after:duration-300 hover:after:w-full"
              >
                {localization?.CreateYourAccount || "Create Your Account"}
              </Link>
            </div>
          </div>
        </div>

        {/* Tenant Modal */}
        {openDialog && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] backdrop-blur-sm"
            style={{ animation: "fadeIn 0.3s ease-out" }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-[450px] overflow-hidden"
              style={{
                animation:
                  "scaleIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
              }}
            >
              <div className="p-5 bg-[#f5f2ec] border-b border-opacity-5">
                <h3 className="text-lg font-semibold text-[var(--primaryColor)] m-0">
                  {localization?.ChangeTenant || "Change Tenant"}
                </h3>
              </div>
              <div className="p-6">
                <div className="mb-0">
                  <label
                    className="block mb-[10px] text-[#2c2419] font-semibold text-[15px]"
                    htmlFor="tenantname"
                  >
                    {localization?.TenancyName || "Tenancy Name"}
                  </label>
                  <input
                    id="tenantname"
                    type="text"
                    className={`w-full py-4 px-[18px] border-2 rounded-2xl text-base font-normal transition-all duration-400 bg-white text-[#2c2419] focus:outline-none focus:border-[var(--primaryColor)] focus:shadow-[0_0_0_4px_var(--boxShadow)] ${tenantErr
                      ? "border-[#e74c3c] bg-[#fdf2f2]"
                      : "border-[#f5f2ec]"
                      }`}
                    value={inputValue || ""}
                    onChange={handleChangeTenantInputBox}
                  />
                  {tenantErr && (
                    <div className="text-[#e74c3c] text-[13px] mt-[6px] font-medium">
                      {tenantErr}
                    </div>
                  )}
                </div>
                {/* <div className="flex items-center gap-3 cursor-pointer mt-5">
                  <input
                    id="clear-tenant"
                    type="checkbox"
                    onChange={(e) => {
                      setChecked(e.target.checked || false);
                      if (e.target.checked) {
                        setInputValue("");
                        setTenant(null);
                      }
                    }}
                    checked={inputValue?.length === 0 || checked}
                    className="w-5 h-5 border-2 border-[#f5f2ec] rounded-md cursor-pointer appearance-none bg-white transition-all duration-300 relative checked:bg-[var(--primaryColor)] checked:border-[var(--primaryColor)] after:content-['✓'] after:absolute after:top-1/2 after:left-1/2 after:transform after:-translate-x-1/2 after:-translate-y-1/2 after:text-white after:text-xs after:font-bold after:opacity-0 checked:after:opacity-100"
                  />
                  <label
                    htmlFor="clear-tenant"
                    className="text-[#5d5448] text-[15px] font-medium cursor-pointer"
                  >
                    {localization?.Clear || "Clear"}
                  </label>
                </div> */}
              </div>
              <div className="p-4 bg-[#f5f2ec] border-t border-opacity-5 flex justify-end gap-3">
                <button
                  type="button"
                  className="py-[10px] px-4 rounded-xl text-sm font-semibold cursor-pointer transition-all duration-300 bg-transparent border border-[#f5f2ec] text-[#5d5448] hover:bg-[#f5f2ec]"
                  onClick={() => { handleCloseDialog(); }}
                >
                  {localization?.Cancel || "Cancel"}
                </button>
                <button
                  type="button"
                  className="py-[10px] px-4 rounded-xl text-sm font-semibold cursor-pointer transition-all duration-300 bg-gradient-to-br from-[var(--primaryColor)] to-[var(--charcoal)] border-none text-white hover:-translate-y-[2px] hover:shadow-md"
                  onClick={handleSaveTenant}
                >
                  {localization?.Save || "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
